<template>
  <v-main>
    <v-container fluid>
      <!-- Combined Header and Filtration Section -->
      <v-row class="mb-2">
        <v-col cols="12">
          <v-card elevation="6" rounded="xxl">
            <!-- Page Header -->
            <page-header
              :title="isDeletePage() != true ? 'Bank Details Management' : 'Deleted Bank Details'"
              :subtitle="isDeletePage() != true ? 'Manage and monitor all bank account details' : 'Restore or permanently delete bank details'"
            >
              <template v-slot:actions>
                <div v-if="isDeletePage() != true" class="d-flex justify-end align-center">
                    <v-btn
                      color="success"
                      elevation="1"
                      small
                      @click="exportToExcel"
                      :loading="isExcelloading"
                      class="mr-2 px-3"
                    >
                      <v-icon left x-small>mdi-file-excel</v-icon>
                      Excel
                    </v-btn>
                    <v-btn
                      color="primary"
                      elevation="1"
                      small
                      to="/bank-details/create"
                      class="px-3"
                    >
                      <v-icon left x-small>mdi-plus</v-icon>
                      Add Bank Detail
                    </v-btn>
                  </div>
              </template>
            </page-header>

            <!-- Filtration Section -->
            <v-card-subtitle class="pb-1 pt-2 px-4">
              <v-row class="w-100 ma-0" align="center">
                <v-col cols="12" md="6" class="pa-0">
                  <div class="d-flex align-center">
                    <v-icon class="mr-2" color="primary" size="20"
                      >mdi-filter-variant</v-icon
                    >
                    <span class="text-body-2 font-weight-medium"
                      >Filter Bank Details</span
                    >
                  </div>
                </v-col>
                <v-col cols="12" md="6" class="pa-0">
                  <div class="d-flex align-center justify-end">
                    <v-chip
                      v-if="hasActiveFilters"
                      color="info"
                      x-small
                      class="mr-2"
                    >
                      <v-icon left x-small>mdi-filter</v-icon>
                      {{ getActiveFiltersCount() }} Active
                    </v-chip>
                    <v-btn
                      text
                      x-small
                      color="error"
                      @click="clearAllFilters"
                      :disabled="!hasActiveFilters"
                    >
                      <v-icon left x-small>mdi-filter-remove</v-icon>
                      Clear
                    </v-btn>
                  </div>
                </v-col>
              </v-row>
            </v-card-subtitle>

            <v-card-text class="px-4 pb-4">
              <v-row>
                <!-- Search Field -->
                <v-col cols="12" md="3">
                  <v-text-field
                    v-model="filter.search_keyword"
                    label="Search Bank Details"
                    prepend-inner-icon="mdi-magnify"
                    solo
                    rounded
                    dense
                    clearable
                    placeholder="Account name, bank, account number..."
                    hide-details
                    @click:clear="
                      (filter.search_keyword = null), (page = 1), getData()
                    "
                    @keydown.enter="(page = 1), getData()"
                  ></v-text-field>
                </v-col>

                <!-- Status Filter -->
                <v-col cols="12" md="2">
                  <v-autocomplete
                    v-model="filter.is_active"
                    :items="statuses"
                    label="Status"
                    prepend-inner-icon="mdi-toggle-switch"
                    solo
                    rounded
                    dense
                    clearable
                    hide-details
                    @change="(page = 1), getData()"
                  ></v-autocomplete>
                </v-col>

                <!-- Account Status Filter -->
                <v-col cols="12" md="2">
                  <v-autocomplete
                    v-model="filter.status"
                    :items="accountStatuses"
                    label="Account Status"
                    prepend-inner-icon="mdi-bank"
                    solo
                    rounded
                    dense
                    clearable
                    hide-details
                    @change="(page = 1), getData()"
                  ></v-autocomplete>
                </v-col>

                <!-- User Filter -->
                <v-col cols="12" md="2">
                  <v-autocomplete
                    v-model="filter.user_id"
                    :items="users"
                    label="User"
                    prepend-inner-icon="mdi-account"
                    solo
                    rounded
                    dense
                    clearable
                    hide-details
                    @change="(page = 1), getData()"
                  ></v-autocomplete>
                </v-col>

                <!-- Bank Filter -->
                <v-col cols="12" md="3">
                  <v-autocomplete
                    v-model="filter.bank_name"
                    :items="bankNames"
                    label="Bank"
                    prepend-inner-icon="mdi-bank"
                    solo
                    rounded
                    dense
                    clearable
                    hide-details
                    @change="(page = 1), getData()"
                  ></v-autocomplete>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Data Table Section -->
      <v-row>
        <v-col cols="12">
          <v-card elevation="6" rounded="xxl">
            <v-card-text class="pa-0">
              <!-- Loading State -->
              <div v-if="isLoading" class="text-center pa-4">
                <health-loader message="Loading bank details..." />
              </div>

              <!-- Data Table -->
              <v-data-table
                v-else
                :headers="headers"
                :items="bankDetails"
                :server-items-length="count"
                :page.sync="page"
                :items-per-page="itemsPerPage"
                hide-default-footer
                fixed-header
                height="285"
                class="enhanced-table"
              >
                <!-- Serial Number Column -->
                <template #[`item.sr_no`]="{ index }">
                  <span class="font-weight-medium primary--text">
                    {{ (page - 1) * itemsPerPage + index + 1 }}
                  </span>
                </template>

                <!-- Status Column -->
                <template #[`item.is_active`]="{ item }">
                  <v-chip
                    :color="item.is_active ? 'success' : 'error'"
                    small
                    dark
                  >
                    <v-icon left x-small>
                      {{
                        item.is_active ? "mdi-check-circle" : "mdi-close-circle"
                      }}
                    </v-icon>
                    {{ item.is_active ? "Active" : "Inactive" }}
                  </v-chip>
                </template>

                <!-- Account Info Column -->
                <template #[`item.account_info`]="{ item }">
                  <div class="d-flex align-center">
                    <v-avatar size="32" class="mr-2" color="primary">
                      <v-icon color="white" size="18">mdi-bank</v-icon>
                    </v-avatar>
                    <div>
                      <div class="text-body-2 font-weight-medium">
                        {{ item.account_name || "No account name" }}
                      </div>
                      <div class="text-caption grey--text">
                        {{ formatAccountNumber(item.account_number) }}
                      </div>
                    </div>
                  </div>
                </template>

                <!-- Bank Info Column -->
                <template #[`item.bank_info`]="{ item }">
                  <div>
                    <div class="text-body-2 font-weight-medium">
                      {{ item.bank_name || "No bank" }}
                    </div>
                    <div class="text-caption grey--text">
                      {{ item.branch || "No branch" }}
                    </div>
                    <div class="text-caption grey--text">
                      {{ item.ifsc_code || "No IFSC" }}
                    </div>
                  </div>
                </template>

                <!-- User Info Column -->
                <template #[`item.user_info`]="{ item }">
                  <div v-if="item.user">
                    <div class="text-body-2 font-weight-medium">
                      {{ item.user.first_name }} {{ item.user.last_name }}
                    </div>
                    <div class="text-caption grey--text">
                      {{ item.user.email }}
                    </div>
                  </div>
                  <div v-else class="text-caption grey--text">
                    No user assigned
                  </div>
                </template>

                <!-- Account Status Column -->
                <template #[`item.status`]="{ item }">
                  <v-chip
                    :color="item.status ? 'success' : 'warning'"
                    small
                    dark
                  >
                    <v-icon left x-small>
                      {{ item.status ? "mdi-check-circle" : "mdi-clock" }}
                    </v-icon>
                    {{ item.status ? "Active" : "Inactive" }}
                  </v-chip>
                </template>

                <!-- Account Type Column -->
                <template #[`item.account_type`]="{ item }">
                  <v-chip
                    v-if="item.account_type"
                    color="info"
                    x-small
                    outlined
                  >
                    {{ item.account_type.description }}
                  </v-chip>
                  <div v-else class="text-caption grey--text">No type</div>
                </template>

                <!-- Created Date Column -->
                <template #[`item.created_at`]="{ item }">
                  <div class="text-caption">
                    {{ formatDate(item.created_at) }}
                  </div>
                </template>

                <!-- Actions Column -->
                <template #[`item.actions`]="{ item }">
                  <div class="d-flex justify-center">
                    <v-btn
                      v-if="!isDeletePage()"
                      icon
                      x-small
                      color="primary"
                      :to="`/bank-details/${item.id}`"
                      class="mr-1"
                    >
                      <v-icon size="16">mdi-pencil</v-icon>
                    </v-btn>
                    <v-btn
                      v-if="!isDeletePage()"
                      icon
                      x-small
                      color="error"
                      @click="confirmDelete(item)"
                      class="mr-1"
                    >
                      <v-icon size="16">mdi-delete</v-icon>
                    </v-btn>
                    <v-btn
                      v-if="isDeletePage()"
                      icon
                      x-small
                      color="success"
                      @click="restoreBankDetail(item.id)"
                      class="mr-1"
                    >
                      <v-icon size="16">mdi-restore</v-icon>
                    </v-btn>
                    <v-btn
                      v-if="isDeletePage()"
                      icon
                      x-small
                      color="error"
                      @click="permanentlyDelete(item.id)"
                    >
                      <v-icon size="16">mdi-delete-forever</v-icon>
                    </v-btn>
                  </div>
                </template>

                <!-- No data state -->
                <template #no-data>
                  <div class="text-center pa-4">
                    <v-icon size="64" color="grey lighten-2" class="mb-2">
                      mdi-bank-off
                    </v-icon>
                    <p class="text-h6 grey--text">No bank details found</p>
                    <p class="text-body-2 grey--text">
                      {{
                        hasActiveFilters
                          ? "Try adjusting your filters"
                          : "Get started by adding a new bank detail"
                      }}
                    </p>
                  </div>
                </template>
              </v-data-table>
            </v-card-text>

            <!-- Pagination Section -->
            <v-card-actions class="justify-center pa-4">
              <v-container>
                <v-row align="center" justify="space-between">
                  <v-col cols="12" md="6">
                    <div class="d-flex align-center">
                      <span class="text-body-2 mr-2">Items per page:</span>
                      <v-select
                        v-model="itemsPerPage"
                        :items="[10, 25, 50, 100]"
                        dense
                        outlined
                        hide-details
                        style="width: 80px"
                        @change="getData"
                      ></v-select>
                      <v-spacer></v-spacer>
                      <span class="text-body-2 ml-4">
                        Showing {{ (page - 1) * itemsPerPage + 1 }} to
                        {{ Math.min(page * itemsPerPage, count) }}
                        of {{ count }} entries
                      </span>
                    </div>
                  </v-col>
                  <v-col cols="12" md="6">
                    <div class="d-flex justify-end">
                      <v-pagination
                        v-model="page"
                        :length="Math.ceil(count / itemsPerPage)"
                        :total-visible="7"
                        @input="getData"
                        color="primary"
                        circle
                      ></v-pagination>
                    </div>
                  </v-col>
                </v-row>
              </v-container>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>

      <!-- Delete Confirmation Dialog -->
      <v-dialog v-model="showDeleteDialog" max-width="500" persistent>
        <v-card>
          <v-card-title class="headline error white--text d-flex align-center">
            <v-icon color="white" class="mr-2">mdi-alert-circle</v-icon>
            Confirm Delete
          </v-card-title>

          <v-card-text class="pt-4">
            <div class="text-center">
              <v-icon size="64" color="error" class="mb-3"
                >mdi-delete-alert</v-icon
              >
              <h3 class="mb-3">
                Are you sure you want to delete this bank detail?
              </h3>

              <div
                class="bank-detail-info-card pa-3 ma-2"
                style="background: #f5f5f5; border-radius: 8px"
              >
                <div class="d-flex align-center">
                  <v-avatar size="48" class="mr-3" color="primary">
                    <v-icon color="white" size="24">mdi-bank</v-icon>
                  </v-avatar>
                  <div class="text-left">
                    <div class="font-weight-bold text-body-1">
                      {{
                        bankDetailToDelete
                          ? bankDetailToDelete.account_name
                          : ""
                      }}
                    </div>
                    <div class="text-caption grey--text">
                      Bank:
                      {{
                        bankDetailToDelete ? bankDetailToDelete.bank_name : ""
                      }}
                    </div>
                    <div class="text-caption grey--text">
                      Account:
                      {{
                        bankDetailToDelete
                          ? formatAccountNumber(
                              bankDetailToDelete.account_number
                            )
                          : ""
                      }}
                    </div>
                  </div>
                </div>
              </div>

              <p class="text-body-2 grey--text mt-3 mb-1">
                <strong>Warning:</strong> This action will move the bank detail
                to trash.
              </p>
              <p class="text-body-2 grey--text">
                You can restore it later if needed.
              </p>
            </div>
          </v-card-text>

          <v-card-actions class="px-4 pb-4">
            <v-spacer></v-spacer>
            <v-btn
              color="grey darken-1"
              text
              @click="showDeleteDialog = false"
              :disabled="isDeleting"
            >
              <v-icon left>mdi-close</v-icon>
              Cancel
            </v-btn>
            <v-btn
              color="error"
              @click="deleteBankDetail"
              :loading="isDeleting"
              :disabled="isDeleting"
            >
              <v-icon left>mdi-delete</v-icon>
              Delete Bank Detail
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-container>
  </v-main>
</template>

<script>
import axios from 'axios';

export default {
  name: "BankDetailsIndex",
  data() {
    return {
      // Data table
      bankDetails: [],
      count: 0,
      page: 1,
      itemsPerPage: 15,

      // Filters
      filter: {
        search_keyword: null,
        is_active: null,
        status: null,
        user_id: null,
        bank_name: null,
        show_deleted: 0,
      },

      // Loading states
      isLoading: false,
      isDeleting: false,
      isExcelloading: false,

      // UI states
      showDeleteDialog: false,
      bankDetailToDelete: null,

      // Filter options
      statuses: [
        { text: "Active", value: 1 },
        { text: "Inactive", value: 0 },
      ],
      accountStatuses: [
        { text: "Active", value: 1 },
        { text: "Inactive", value: 0 },
      ],
      users: [],
      bankNames: [
        { text: "State Bank of India", value: "State Bank of India" },
        { text: "HDFC Bank", value: "HDFC Bank" },
        { text: "ICICI Bank", value: "ICICI Bank" },
        { text: "Axis Bank", value: "Axis Bank" },
        { text: "Punjab National Bank", value: "Punjab National Bank" },
        { text: "Bank of Baroda", value: "Bank of Baroda" },
        { text: "Canara Bank", value: "Canara Bank" },
        { text: "Union Bank of India", value: "Union Bank of India" },
      ],

      // Table headers
      headers: [
        {
          text: "#",
          value: "sr_no",
          sortable: false,
          width: "50px",
          align: "center",
        },
        {
          text: "Account Info",
          value: "account_info",
          sortable: false,
          width: "180px",
        },
        {
          text: "Bank Info",
          value: "bank_info",
          sortable: false,
          width: "180px",
        },
        {
          text: "User",
          value: "user_info",
          sortable: false,
          width: "150px",
        },
        {
          text: "Account Status",
          value: "status",
          sortable: false,
          width: "100px",
        },
        {
          text: "Account Type",
          value: "account_type",
          sortable: false,
          width: "110px",
        },
        {
          text: "Created",
          value: "created_at",
          sortable: false,
          width: "80px",
        },
        { text: "Status", value: "is_active", sortable: false, width: "80px" },
        {
          text: "Actions",
          value: "actions",
          sortable: false,
          width: "110px",
          align: "center",
        },
      ],
    };
  },

  computed: {
    hasActiveFilters() {
      return (
        this.filter.search_keyword ||
        this.filter.is_active !== null ||
        this.filter.status !== null ||
        this.filter.user_id ||
        this.filter.bank_name
      );
    },
  },

  watch: {
    page: {
      handler: function (newPage) {
        this.jumpToPage = newPage;
        this.getData();
      },
    },
    itemsPerPage: "getData",
    $route() {
      this.getData();
    },
  },

  mounted() {
    this.jumpToPage = this.page;
    this.getData();
    this.getUsers();
  },

  methods: {
    async getData() {
      console.log("Get Bank Details Data");
      this.filter.show_deleted = this.$route.query.open_trash ? 1 : 0;
      this.isLoading = true;

      try {
        const params = {
          ...this.filter,
          page: this.page,
          rowsPerPage: this.itemsPerPage,
        };

        Object.keys(params).forEach((key) => {
          if (params[key] === null || params[key] === "") {
            delete params[key];
          }
        });

        const response = await axios.get("/bank_details", { params });

        if (response.data.success) {
          this.bankDetails = response.data.data;
          this.count = response.data.count;
        } else {
          this.$store.dispatch("errors/setError", {
            message: response.data.message || "Failed to fetch bank details",
          });
        }
      } catch (error) {
        console.error(error);
        this.$store.dispatch("errors/setError", {
          message: "Error loading bank details data",
        });
      } finally {
        this.isLoading = false;
      }
    },

    async getUsers() {
      try {
        const response = await axios.get("/users", {
          params: { is_active: 1, show_deleted: 0 },
        });
        if (response.data.success) {
          this.users = response.data.data.map((user) => ({
            text: `${user.first_name} ${user.last_name} (${user.email})`,
            value: user.id,
          }));
        }
      } catch (error) {
        console.log("Error loading users:", error);
      }
    },

    isDeletePage() {
      return this.$route.query.open_trash === "true";
    },

    getActiveFiltersCount() {
      let count = 0;
      if (this.filter.search_keyword) count++;
      if (this.filter.is_active !== null) count++;
      if (this.filter.status !== null) count++;
      if (this.filter.user_id) count++;
      if (this.filter.bank_name) count++;
      return count;
    },

    clearAllFilters() {
      this.filter = {
        search_keyword: null,
        is_active: null,
        status: null,
        user_id: null,
        bank_name: null,
        show_deleted: this.filter.show_deleted,
      };
      this.page = 1;
      this.getData();
    },

    confirmDelete(bankDetail) {
      this.bankDetailToDelete = bankDetail;
      this.showDeleteDialog = true;
    },

    async deleteBankDetail() {
      if (!this.bankDetailToDelete) return;

      this.isDeleting = true;
      try {
        const response = await axios.post(
          `/bank_details/delete/${this.bankDetailToDelete.id}`
        );

        if (response.data.success) {
          this.$toast.success("Bank detail deleted successfully");
          this.getData();
        } else {
          this.$store.dispatch("errors/setError", {
            message: response.data.message || "Failed to delete bank detail",
          });
        }
      } catch (error) {
        console.error(error);
        this.$store.dispatch("errors/setError", {
          message: "Error deleting bank detail",
        });
      } finally {
        this.isDeleting = false;
        this.showDeleteDialog = false;
        this.bankDetailToDelete = null;
      }
    },

    async restoreBankDetail(id) {
      try {
        const response = await axios.post(`/bank_details/restore/${id}`);

        if (response.data.success) {
          this.$toast.success("Bank detail restored successfully");
          this.getData();
        } else {
          this.$store.dispatch("errors/setError", {
            message: response.data.message || "Failed to restore bank detail",
          });
        }
      } catch (error) {
        console.error(error);
        this.$store.dispatch("errors/setError", {
          message: "Error restoring bank detail",
        });
      }
    },

    async permanentlyDelete(id) {
      if (
        confirm("Are you sure you want to permanently delete this bank detail?")
      ) {
        try {
          const response = await axios.delete(`/bank_details/${id}`);

          if (response.data.success) {
            this.$toast.success("Bank detail permanently deleted");
            this.getData();
          } else {
            this.$store.dispatch("errors/setError", {
              message: response.data.message || "Failed to permanently delete",
            });
          }
        } catch (error) {
          console.error(error);
          this.$store.dispatch("errors/setError", {
            message: "Error permanently deleting bank detail",
          });
        }
      }
    },

    async exportToExcel() {
      this.isExcelloading = true;
      try {
        const params = { ...this.filter };
        Object.keys(params).forEach((key) => {
          if (params[key] === null || params[key] === "") {
            delete params[key];
          }
        });

        const response = await axios.get("/bank_details/excel", {
          params,
          responseType: "blob",
        });

        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute(
          "download",
          `bank_details_${new Date().toISOString().slice(0, 10)}.xlsx`
        );
        document.body.appendChild(link);
        link.click();
        link.remove();

        this.$toast.success("Bank details exported successfully");
      } catch (error) {
        console.error(error);
        this.$store.dispatch("errors/setError", {
          message: "Error exporting bank details",
        });
      } finally {
        this.isExcelloading = false;
      }
    },

    formatDate(dateString) {
      if (!dateString) return "N/A";
      const date = new Date(dateString);
      return date.toLocaleDateString();
    },

    formatAccountNumber(accountNumber) {
      if (!accountNumber) return "N/A";

      const length = accountNumber.length;
      if (length <= 4) return accountNumber;

      // Show last 4 digits, mask the rest
      const maskedLength = length - 4;
      const masked = "*".repeat(maskedLength);
      const lastFour = accountNumber.slice(-4);

      return masked + lastFour;
    },
  },
};
</script>

<style scoped>
/* Enhanced table styling */
.enhanced-table >>> .v-data-table__wrapper {
  border-radius: 12px;
}

.enhanced-table >>> .v-data-table-header {
  background-color: #f8f9fa;
}

.enhanced-table >>> .v-data-table-header th {
  font-weight: 600;
  font-size: 0.75rem;
  color: #495057;
  border-bottom: 2px solid #dee2e6;
}

.enhanced-table >>> tbody tr:hover {
  background-color: #f8f9fa !important;
}

.enhanced-table >>> tbody tr td {
  font-size: 0.8rem;
}

/* Bank detail info card */
.bank-detail-info-card {
  border: 1px solid #e0e0e0;
  transition: all 0.2s ease;
}

.bank-detail-info-card:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

/* Responsive design */
@media (max-width: 768px) {
  .enhanced-table >>> .v-data-table-header th {
    font-size: 0.7rem;
    padding: 0 8px;
  }

  .enhanced-table >>> tbody tr td {
    font-size: 0.75rem;
    padding: 0 8px;
  }
}
</style>